import React, { useRef, useState, useEffect } from 'react';

import {
  Typography,
  Box,
  Container,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../../redux/reducers/cartSlice';
import { shades } from '../../../theme';
import mtreatHome from '../../data/home/mHome.jpg';

const FeaturedDrugs = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width:600px)');

  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart.cart);
  const cartError = useSelector((state) => state.cart.error);

  // Fetch drugs on component mount
  useEffect(() => {
    fetchDrugs();
  }, []);

  const fetchDrugs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/medication/drug/?_=${Date.now()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch drugs');
      }

      const data = await response.json();

      // Filter out deleted drugs and limit to featured/top drugs
      const activeDrugs = data.filter((drug) => !drug.is_deleted).slice(0, 6); // Take first 6 drugs

      setDrugs(activeDrugs);
    } catch (err) {
      console.error('Error fetching drugs:', err);
      setError('Failed to load featured drugs.');
    } finally {
      setLoading(false);
    }
  };

  // Function to determine stock status
  const getStockStatus = (stock, reorderLevel) => {
    if (stock <= 0) return 'Out of Stock';
    if (stock <= reorderLevel) return 'Low Stock';
    return 'In Stock';
  };

  const handleAddToCart = (drug) => {
    dispatch(
      addToCart({
        item: {
          id: drug.id,
          name: drug.name,
          price: drug.price,
          currency: drug.currency,
          img: drug.img || mtreatHome,
          stock: drug.stock,
          count: 1
        },
        message: `${drug.name} added to cart`
      })
    );
  };

  const scrollRef = useRef(null);

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = direction === 'left' ? -300 : 300;

      // Get current scroll position
      const startPosition = container.scrollLeft;
      const targetPosition = startPosition + scrollAmount;

      container.scrollTo({
        left: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) =>
      index < Math.floor(rating) ? '★' : '☆'
    ).join('');
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3, mt: 4 }}>
      <Box
        sx={{
				  display: 'flex',
				  alignItems: 'center',
				  mb: 2,
				  justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography
            variant={isMobile ? 'body2' : 'body1'}
            sx={{
						  fontWeight: 600,
						  color: isDarkMode ? shades.white[100] : shades.secondary[500],
						  backgroundColor: isDarkMode
						    ? shades.neutral[800]
						    : shades.neutral[200],
						  display: 'inline-block',
						  px: 1.5,
						  py: 0.5,
						  borderRadius: 1,
						  mb: 1,
						  fontSize: isMobile ? '0.7rem' : '0.75rem'
            }}
          >
            Best Selling Drugs
          </Typography>
          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            sx={{
						  fontWeight: 600,
						  color: isDarkMode ? shades.white[100] : shades.secondary[500]
            }}
          >
            Featured Drugs
          </Typography>
        </Box>
        <Box>
          <IconButton
            onClick={() => handleScroll('left')}
            sx={{
						  color: isDarkMode ? shades.white[100] : shades.secondary[500],
						  '&:hover': {
						    backgroundColor: isDarkMode
						      ? shades.neutral[700]
						      : shades.neutral[200]
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => handleScroll('right')}
            sx={{
						  color: isDarkMode ? shades.white[100] : shades.secondary[500],
						  '&:hover': {
						    backgroundColor: isDarkMode
						      ? shades.neutral[700]
						      : shades.neutral[200]
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
      <Box
        ref={scrollRef}
        sx={{
				  display: 'flex',
				  gap: 2,
				  overflowX: 'auto',
				  scrollBehavior: 'smooth',
				  position: 'relative',
				  '&::-webkit-scrollbar': { display: 'none' },
				  scrollbarWidth: 'none',
				  msOverflowStyle: 'none',
				  pb: 1
        }}
      >
        {loading ? (
          <Box
            sx={{
						  display: 'flex',
						  justifyContent: 'center',
						  width: '100%',
						  py: 4
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography
            color='error'
            sx={{ width: '100%', textAlign: 'center', py: 4 }}
          >
            {error}
          </Typography>
        ) : (
				  drugs.map((drug) => (
  <Box
    key={drug.id}
    sx={{
							  flex: '0 0 auto',
							  width: isMobile ? 160 : 250,
							  position: 'relative',
							  p: 2,
							  borderRadius: 2,
							  backgroundColor: isDarkMode
							    ? shades.secondary[800]
							    : shades.white[100],
							  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
							  transition: 'transform 0.2s, box-shadow 0.2s',
							  '&:hover': {
							    transform: 'translateY(-4px)',
							    boxShadow: isDarkMode
							      ? '0 4px 12px rgba(0,0,0,0.3)'
							      : '0 4px 12px rgba(0,0,0,0.1)'
      }
    }}
  >
    {drug.discount_percentage && (
      <Box
        sx={{
									  position: 'absolute',
									  top: 10,
									  left: 10,
									  backgroundColor: shades.neutral[700],
									  color: shades.white[100],
									  px: 1,
									  py: 0.5,
									  borderRadius: 1,
									  fontSize: '0.75rem'
        }}
      >
        {`-${drug.discount_percentage || '0'}%`}
      </Box>
    )}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
      <IconButton
        size='small'
        onClick={() => handleAddToCart(drug)}
        disabled={drug.stock === 0}
        sx={{
									  color:
											drug.stock === 0
											  ? '#cccccc'
											  : isDarkMode
											    ? shades.white[100]
											    : shades.secondary[500],
									  '&:hover': {
									    backgroundColor: isDarkMode
									      ? 'rgba(255,255,255,0.1)'
									      : 'rgba(0,0,0,0.05)',
									    transform: drug.stock === 0 ? 'none' : 'translateY(-1px)'
          },
									  '&.Mui-disabled': {
									    color: '#cccccc',
									    opacity: 0.5
          }
        }}
      >
        <AddShoppingCartIcon fontSize='small' />
      </IconButton>

      {/* Error display */}
      {cartError && cartError.drugId === drug.id && (
        <Alert
          severity='error'
          sx={{
										  mt: 1,
										  fontSize: '0.7rem',
										  py: 0,
										  width: 'fit-content',
										  position: 'absolute',
										  bottom: -30,
										  left: '50%',
										  transform: 'translateX(-50%)',
										  zIndex: 10
          }}
        >
          {cartError.message}
        </Alert>
      )}
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      <img
        src={drug.img || mtreatHome}
        alt={drug.name}
        style={{
									  maxWidth: '100%',
									  maxHeight: '120px',
									  objectFit: 'contain'
        }}
      />
    </Box>
    <Typography
      variant='body2'
      sx={{
								  fontWeight: 600,
								  mb: 0.5,
								  color: isDarkMode ? shades.white[100] : shades.secondary[500]
      }}
    >
      {drug.name}
    </Typography>
    <Box
      sx={{
								  display: { xs: 'block', md: 'flex' },
								  justifyContent: 'space-between',
								  alignItems: 'center'
      }}
    >
      <Box>
        {drug.original_price && (
          <Typography
            variant='body2'
            sx={{
											  textDecoration: 'line-through',
											  color: isDarkMode
											    ? shades.neutral[400]
											    : shades.neutral[500],
											  fontSize: {
											    sm: 12,
											    md: 14,
											    lg: 16,
											    xl: 18
              },
											  lineHeight: 1.2
            }}
          >
            {drug.currency} {drug.original_price || drug.price}
          </Typography>
        )}
        <Typography
          variant='body1'
          sx={{
										  fontWeight: 600,
										  color: isDarkMode
										    ? shades.accent[300]
										    : shades.accent[700],
										  fontSize: {
										    sm: 12,
										    md: 14,
										    lg: 16,
										    xl: 18
            },
										  lineHeight: 1.2,
										  mt: 0.5
          }}
        >
          {drug.currency} {drug.price}
        </Typography>
      </Box>
      <Typography
        variant='body1'
        sx={{
									  display: 'flex',
									  alignItems: 'center',
									  fontSize: 14,
									  lineHeight: 1.2,
									  marginTop: { xs: 1, md: 0 },
									  color: isDarkMode
									    ? shades.white[100]
									    : shades.secondary[500]
        }}
      >
        <Box
          component='span'
          sx={{ color: isDarkMode ? '#FFFF00' : '#FFDF00' }}
        >
          {renderStars(drug.average_rating || 0)}
        </Box>
									&nbsp;({drug.total_ratings || 0})
      </Typography>
    </Box>
  </Box>
				  ))
        )}
      </Box>
    </Container>
  );
};

export default FeaturedDrugs;
